import { APP_NAME } from "@/lib/app-config"

const PROTECTED_TOKENS = new Set([
    "API",
    "B2B",
    "CMR",
    "EPAL",
    "EUR",
    "GDPR",
    "ID",
    "ISO",
    "KPI",
    "KYB",
    "LKW",
    "N/A",
    "OTP",
    "PDF",
    "PNG",
    "Q1",
    "Q2",
    "Q3",
    "Q4",
    "QR",
    "RFQ",
    "RON",
    "SMS",
    "SRL",
    "URL",
    "VAT",
    "2FA",
    APP_NAME,
])

const SENTENCE_ENDINGS = new Set([".", "!", "?"])

const stripDiacritics = (value: string): string => {
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

const shouldPreserveWord = (word: string): boolean => {
    if (!word) return true
    if (word.includes("@") || word.includes("://") || word.includes("www.")) return true
    if (/^\{\{.*\}\}$/.test(word) || /^\$\{.*\}$/.test(word)) return true
    if (/^\+?\d+$/.test(word)) return true
    if (/^[A-Z0-9][A-Z0-9\-_/+.]*$/.test(word)) return true
    if (PROTECTED_TOKENS.has(word)) return true
    if (/\b[A-Z][a-z]+[A-Z][A-Za-z]*\b/.test(word)) return true
    return false
}

const normalizeWordCase = (word: string): string => {
    if (shouldPreserveWord(word)) return word
    return word.toLowerCase()
}

const toSentenceCase = (value: string): string => {
    let shouldCapitalize = true
    let result = ""

    for (let i = 0; i < value.length; i += 1) {
        const char = value[i]

        if (/[A-Za-z]/.test(char)) {
            if (shouldCapitalize) {
                result += char.toUpperCase()
                shouldCapitalize = false
            } else {
                result += char
            }
            continue
        }

        result += char

        if (SENTENCE_ENDINGS.has(char)) {
            shouldCapitalize = true
        }
    }

    return result
}

const normalizeRomanianString = (input: string): string => {
    if (!input.trim()) return input

    const withoutDiacritics = stripDiacritics(input)

    if (withoutDiacritics.includes("://") || withoutDiacritics.includes("@")) {
        return withoutDiacritics
    }

    const normalizedWords = withoutDiacritics.replace(/\b[0-9A-Za-z@._:+\-/]+\b/g, normalizeWordCase)
    return toSentenceCase(normalizedWords)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

export function normalizeRomanianContent<T>(value: T): T {
    if (typeof value === "string") {
        return normalizeRomanianString(value) as T
    }

    if (Array.isArray(value)) {
        return value.map((item) => normalizeRomanianContent(item)) as T
    }

    if (isPlainObject(value)) {
        const output: Record<string, unknown> = {}
        for (const [key, nestedValue] of Object.entries(value)) {
            output[key] = normalizeRomanianContent(nestedValue)
        }
        return output as T
    }

    return value
}

export function normalizeRomanianLocaleMap<T extends { ro: unknown }>(translations: T): T {
    return {
        ...translations,
        ro: normalizeRomanianContent(translations.ro),
    }
}
